import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import {
  AppParametersService,
  BaseAppParameters,
  FilterOption,
  Geometry,
  GeometryType,
  Region,
  AutocompleteRegionHit,
  DetailsRegionHit,
  RegionID,
  RegionSearchContainedPostalCodesResponse,
  RegionSearchResponse,
  RegionSearchResult,
  RegionSource,
} from '@immosky/types';

@Injectable({
  providedIn: 'root',
})
export class RegionSearchService {
  constructor(
    private httpClient: HttpClient,
    private appParametersService: AppParametersService<BaseAppParameters>
  ) {}

  search(userQuery: string): Observable<RegionSearchResult[]> {
    const query = {
      query: {
        bool: {
          must: [
            {
              terms: {
                countryIsocode: this.appParametersService
                  .get()
                  .countries.map((x) => x.toUpperCase()),
              },
            },
            {
              multi_match: {
                query: userQuery,
                type: 'phrase_prefix',
                fields: [
                  'keywords.suggest',
                  'keywords.suggest._2gram',
                  'keywords.suggest._3gram',
                  'name.suggest^3',
                  'name.suggest._2gram^3',
                  'name.suggest._3gram^3',
                ],
              },
            },
          ],
          should: [
            {
              multi_match: {
                query: userQuery,
                type: 'most_fields',
                fields: ['name.raw^5', 'name^4'],
              },
            },
            {
              term: {
                placeType: {
                  value: 'city',
                  boost: 3,
                },
              },
            },
            {
              term: {
                placeType: {
                  value: 'village',
                  boost: 3,
                },
              },
            },
            {
              term: {
                placeType: {
                  value: 'state',
                  boost: 2.5,
                },
              },
            },
            {
              term: {
                placeType: {
                  value: 'county',
                  boost: 2,
                },
              },
            },
          ],
        },
      },
      track_scores: true,
      sort: [{ _score: 'desc' }, { 'name.raw': 'asc' }],
      _source: false,
      fields: ['name', 'placeType', 'postalCode'],
      size: 10,
    };

    return this.fetchAutocompleteRegions(query);
  }

  searchByID(ids: RegionID[]): Observable<Region[]> {
    const query = {
      query: {
        ids: {
          values: ids,
        },
      },
    };

    return this.fetchRegionDetails(query) as Observable<Region[]>;
  }

  propertyNameById(id: number): Observable<FilterOption<number>> {
    return this.httpClient
      .get<{ _source: { title: string }; _id: string }>(
        `${this.appParametersService.get().apiBaseUrl}/${
          this.appParametersService.get().propertiesIndex
        }/_doc/${id}?_source_includes=title`
      )
      .pipe(
        map((resp) => {
          return {
            text: resp._source.title,
            value: parseInt(resp._id),
          };
        })
      );
  }

  containedPostalCodesForIds(ids: number[]): Observable<string[]> {
    const query = {
      query: {
        ids: {
          values: ids,
        },
      },
      fields: ['containedPostalCodes'],
      _source: false,
    };
    return this.httpClient
      .post<RegionSearchContainedPostalCodesResponse>(
        `${this.appParametersService.get().apiBaseUrl}/${
          this.appParametersService.get().regionsIndex
        }/_search`,
        query
      )
      .pipe(
        map((response) => {
          return response.hits.hits.reduce((postalCodes: string[], hit) => {
            return [...postalCodes, ...hit.fields.containedPostalCodes];
          }, []);
        })
      );
  }

  // TODO: Typing for query
  private fetchAutocompleteRegions(
    query: unknown
  ): Observable<RegionSearchResult[]> {
    return this.httpClient
      .post<RegionSearchResponse<AutocompleteRegionHit>>(
        `${this.appParametersService.get().apiBaseUrl}/${
          this.appParametersService.get().regionsIndex
        }/_search`,
        query
      )
      .pipe(
        map((response: RegionSearchResponse<AutocompleteRegionHit>) => {
          return response.hits.hits.map((hit: AutocompleteRegionHit) => {
            return {
              id: hit._id,
              name: hit.fields.name[0],
              placeType: hit.fields.placeType[0],
              postalCodes: hit.fields.postalCode,
            };
          });
        }),
        catchError((error) => {
          console.error(error);
          return of([]);
        })
      );
  }

  private fetchRegionDetails(query: unknown): Observable<Region[]> {
    return this.httpClient
      .post<RegionSearchResponse<DetailsRegionHit>>(
        `${this.appParametersService.get().apiBaseUrl}/${
          this.appParametersService.get().regionsIndex
        }/_search`,
        query
      )
      .pipe(
        map((response: RegionSearchResponse<DetailsRegionHit>) => {
          return response.hits.hits.map((hit: DetailsRegionHit) => {
            return this.convertRegionSourceToRegion(hit._source);
          });
        }),
        catchError((error) => {
          console.error(error);
          return of([]);
        })
      );
  }

  private convertRegionSourceToRegion(source: RegionSource): Region {
    return {
      id: source.id,
      name: source.name,
      keywords: source.keywords,
      centroid: {
        lat: source.centroid[1],
        lng: source.centroid[0],
      },
      postalCode: source.postalCode,
      geometry: this.getGeometry(source),
      placeType: source.placeType,
    };
  }

  private getGeometry(source: RegionSource): Geometry {
    switch (source.geometry.type) {
      case GeometryType.POINT:
        return {
          type: source.geometry.type,
          coordinates: {
            lat: source.geometry.coordinates[1],
            lng: source.geometry.coordinates[0],
          },
        };
      case GeometryType.POLYGON:
        return {
          type: source.geometry.type,
          coordinates: source.geometry.coordinates.map((shape) => {
            return shape.map((coordinate) => {
              return {
                lat: coordinate[1],
                lng: coordinate[0],
              };
            });
          }),
        };
      case GeometryType.MULTI_POLYGON:
        return {
          type: source.geometry.type,
          coordinates: source.geometry.coordinates.map((group) => {
            return group.map((shape) => {
              return shape.map((coordinate) => {
                return {
                  lat: coordinate[1],
                  lng: coordinate[0],
                };
              });
            });
          }),
        };
      default:
        return {
          type: GeometryType.NULL,
        };
    }
  }
}
